import { loadSearchCatalog } from "../searchCatalog";
import {
  getAttributeEntityNamesFromSearchEntity,
  getDefaultOrderedByFromSearchEntity,
} from "../searchDefaults";
import { getFieldFromName } from "../searchFields";
import { getAdvancedNodesFromSavedNodes } from "../searchSavedNodes";
import { DeleteView, RenameView, SaveView } from "../../api/searchService";
import { Get, isFailedResponse } from "../../api/request";
import { GetEntityRecordById } from "../../api/entityService";
import { SearchColumn, SearchNode, SearchQuery } from "../advancedSearch.types";
import { buildSearchRequest, getSearchJoins } from "../advancedSearchRequest";

type Dispatch = unknown;
type Allowed = Record<string, string> | null | undefined;

export interface SavedViewSummary {
  Id: string;
  Name: string;
  EntityName: string;
}

export interface SavedViewRef {
  id: string;
  name: string;
}

export interface LoadedSavedView {
  view: SavedViewRef;
  query: SearchQuery;
  /** Fields the view named that no longer exist, so its rules were dropped. */
  unresolved: string[];
}

interface Response<T = unknown> {
  status?: number;
  data?: T;
}

const get = Get as unknown as (dispatch: Dispatch, method: string) => Promise<Response | undefined>;
const getRecord = GetEntityRecordById as unknown as (
  dispatch: Dispatch,
  entityName: string,
  id: string
) => Promise<Response | undefined>;
const save = SaveView as unknown as (
  dispatch: Dispatch,
  view: Record<string, unknown>
) => Promise<Response<string> | undefined>;
const rename = RenameView as unknown as (
  dispatch: Dispatch,
  view: { Id: string; Name: string }
) => Promise<Response | undefined>;
const remove = DeleteView as unknown as (dispatch: Dispatch, id: string) => Promise<Response | undefined>;

/** The legacy lookup held the list for the session, so a change has to drop it. */
const forgetList = () => sessionStorage.removeItem("Saved Views");

export async function listSavedViews(dispatch: Dispatch): Promise<SavedViewSummary[]> {
  const response = await get(dispatch, "GetSavedViews");

  return isFailedResponse(response) || !Array.isArray(response?.data)
    ? []
    : (response!.data as SavedViewSummary[]);
}

/** What a save writes for the view, also compared to detect unsaved changes. */
export function getSavedViewPayload(query: SearchQuery) {
  const request = buildSearchRequest(query);

  return {
    AdvancedSearchNodes: request.AdvancedSearchNodes,
    EntityName: query.entityName,
    Fields: request.Fields,
    Filters: request.Filters,
  };
}

// A stored rule's node type is whatever the service wrote; anything that is not a group is a rule.
const normalise = (nodes: SearchNode[]): SearchNode[] =>
  nodes.map((node) =>
    (node as { children?: SearchNode[] }).children
      ? ({ ...node, nodeType: "Group", children: normalise((node as { children: SearchNode[] }).children) } as SearchNode)
      : ({ ...node, nodeType: "Filter" } as SearchNode)
  );

/** Reads a saved view and rebuilds the search it describes, or null if it could not be read. */
export async function readSavedView(
  dispatch: Dispatch,
  id: string,
  allowedEntityTypes: Allowed
): Promise<LoadedSavedView | null> {
  const response = await getRecord(dispatch, "savedview", id);
  const record = response?.data as
    | {
        Id: string;
        Name: string;
        Fields?: {
          entityname: string;
          fields: { Name: string }[];
          nodes: unknown[];
          filters?: Record<string, string>;
        };
      }
    | undefined;

  if (isFailedResponse(response) || !record?.Fields) {
    return null;
  }

  const { entityname, fields, nodes, filters = {} } = record.Fields;
  const entityFilters = [{ Key: "advancedSearchEntityName", Value: entityname }];
  const joins = getSearchJoins(entityname, allowedEntityTypes);

  // Custom fields come from metadata, so they are cached before the columns and rules are matched.
  await Promise.all(
    getAttributeEntityNamesFromSearchEntity(entityFilters, joins).map((name: string) =>
      loadSearchCatalog(dispatch as never, name)
    )
  );

  const unresolved: string[] = [];
  const columns = fields
    .map((field) => getFieldFromName(entityFilters, joins, field.Name))
    .filter((column): column is SearchColumn => Boolean(column));
  const stored = getAdvancedNodesFromSavedNodes(
    entityFilters,
    nodes,
    { getJoins: () => joins },
    unresolved
  );

  return {
    view: { id: record.Id, name: record.Name },
    unresolved,
    query: {
      entityName: entityname,
      columns,
      nodes: normalise(stored),
      order: {
        by: filters.fieldOrderedBy || getDefaultOrderedByFromSearchEntity(entityname),
        asc: filters.fieldOrderedByAsc === "true",
        secondaryBy: filters.fieldOrderedBySecondary ?? "",
        secondaryAsc: filters.fieldOrderedBySecondaryAsc === "true",
      },
    },
  };
}

/** Creates the view, or updates it when `id` is given. Resolves to its id, or null. */
export async function writeSavedView(
  dispatch: Dispatch,
  query: SearchQuery,
  name: string,
  id?: string
): Promise<string | null> {
  const response = await save(dispatch, { ...getSavedViewPayload(query), Name: name, Id: id });

  if (isFailedResponse(response)) {
    return null;
  }

  forgetList();

  return id ?? String(response?.data);
}

export async function renameSavedView(dispatch: Dispatch, id: string, name: string) {
  const done = !isFailedResponse(await rename(dispatch, { Id: id, Name: name }));

  forgetList();

  return done;
}

export async function deleteSavedView(dispatch: Dispatch, id: string) {
  const done = !isFailedResponse(await remove(dispatch, id));

  forgetList();

  return done;
}
